import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { type FormEvent, useEffect, useRef, useState } from "react";

import { ApiError, submitOnboarding, type CurrentUser } from "../../api/client";
import { roleFullLabel } from "../../lib/role-labels";
import { useCurrentUser } from "../../auth/useCurrentUser";
import { OnboardingShell } from "../../components/OnboardingShell";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { FormError } from "@/components/ui/form-error";

/**
 * Step 1 of the post-invite onboarding wizard — identity.
 *
 * Pre-populates from /auth/me (full_name + company_name + email come from
 * the invite). Locks email always and company_name when present. The spec
 * lists password here too; PRM handles password via the Authentik recovery
 * flow before sign-in (ADR-0010), so it's not captured again.
 *
 * The form lives in a child component (`IdentityForm`) so the `useState`
 * initial-from-props pattern fires AFTER /auth/me has loaded — otherwise
 * the first/last-name fields would render empty on mount and `useState`
 * would ignore the later-arriving `full_name`.
 */
export function OnboardingIdentityStep() {
  const me = useCurrentUser();
  const navigate = useNavigate();

  // CHP-1105: resolve the redirect destination as a value + fire it from a
  // ref-guarded effect instead of returning a render-time <Navigate>, which
  // flashes a frame of the wrong route and can re-navigate on every re-render
  // (the CHP-1203 loop). `null` means "no redirect" — either not decidable yet
  // (auth/me loading) or the user stays in the wizard. The me-fetch error is an
  // inline render, not a redirect, so it contributes `null`. Mirrors
  // ProtectedRoute; decision order preserved (loading → unauth → error → tc).
  const redirectTarget: string | null =
    me.isAuthLoading || me.isLoading
      ? null
      : !me.isAuthenticated
        ? "/login"
        : !me.data || !me.accessToken
          ? null
          : // If the user has already accepted T&Cs, they don't belong here.
            me.data.tc_accepted_at != null
            ? "/dashboard"
            : null;

  const redirected = useRef(false);
  useEffect(() => {
    if (redirectTarget === null || redirected.current) return;
    redirected.current = true;
    void navigate({ to: redirectTarget, replace: true });
  }, [redirectTarget, navigate]);

  if (me.isAuthLoading || me.isLoading) {
    return <p role="status">Loading…</p>;
  }
  // Redirect pending → loader, never a flash of the wrong route.
  if (redirectTarget !== null) {
    return <p role="status">Loading…</p>;
  }
  if (!me.data || !me.accessToken) {
    return <p role="alert">Could not load your account. Try refreshing.</p>;
  }

  return <IdentityForm user={me.data} accessToken={me.accessToken} />;
}

interface IdentityFormProps {
  user: CurrentUser;
  accessToken: string;
}

function IdentityForm({ user, accessToken }: IdentityFormProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const initial = splitFullName(user.full_name ?? "");
  const [firstName, setFirstName] = useState(initial.first);
  const [lastName, setLastName] = useState(initial.last);
  const [phone, setPhone] = useState("");
  // CHP-568: the spec marks Job title "Pre-populated from Role, editable".
  // Prefer a real job title captured earlier in the funnel (the request-access
  // form, threaded onto the profile at invite time and surfaced on /auth/me);
  // otherwise fall back to the invited role's canonical label. Editable either
  // way — this only seeds the field.
  const [position, setPosition] = useState(user.position ?? roleFullLabel(user) ?? "");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [topError, setTopError] = useState<string | null>(null);

  // CHP-1021: who signs vs who inherits within a partner org.
  // - The org's SIGNING PO — a channel_partner who is an org admin AND whose org
  //   has no onboarded PO yet — answers the rewards step and signs the T&Cs (the
  //   full 3-step Flow A). `is_org_admin !== false` defaults null/true to PO
  //   semantics (back-compat); `org_already_registered !== true` means they're
  //   the first PO to onboard.
  // - A NON-SIGNING partner — every BDM (`is_org_admin === false`), and every
  //   additional PO of an already-registered org (`org_already_registered`) —
  //   inherits the org's rewards preference and T&C acceptance (the founding PO
  //   made both on the org's behalf). Identity is their ONLY step; the backend
  //   stamps their acceptance when they submit it.
  // - Admins/managers aren't in a partner org, so they still sign: Identity → T&C.
  const isPartner = user.role === "channel_partner";
  const isSigningPo =
    isPartner && user.is_org_admin !== false && user.org_already_registered !== true;
  const inheritsOrgTerms =
    isPartner && (user.is_org_admin === false || user.org_already_registered === true);

  const nextRoute = isSigningPo
    ? "/onboarding/spif"
    : inheritsOrgTerms
      ? // BDM → dashboard; additional PO → the PO Welcome / invite-team screen.
        user.is_org_admin === false
        ? "/dashboard"
        : "/onboarding/complete"
      : "/onboarding/terms"; // admin / manager
  // CHP-808/CHP-1021: Identity is always step 1; only the strip width varies — 3
  // for the signing PO, 1 for a non-signing partner (strip hidden), 2 for
  // admin/manager. Built as one discriminated value so OnboardingShell accepts it.
  const shell = isSigningPo
    ? ({ totalSteps: 3, step: 1 } as const)
    : inheritsOrgTerms
      ? ({ totalSteps: 1, step: 1 } as const)
      : ({ totalSteps: 2, step: 1 } as const);

  const save = useMutation({
    mutationFn: () =>
      submitOnboarding(accessToken, {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        phone: phone.trim() || null,
        position: position.trim() || null,
      }),
    onSuccess: async () => {
      // A non-signing partner's onboarding completes on the backend the moment
      // identity is submitted (it stamps tc_accepted_at). Refetch /auth/me so the
      // fresh acceptance is in cache BEFORE navigating — otherwise ProtectedRoute
      // (which the dashboard sits behind) reads the stale tc_accepted_at == null
      // and bounces them back into the wizard. Same race fix the terms step uses.
      if (inheritsOrgTerms) {
        await queryClient.refetchQueries({ queryKey: ["auth", "me"] });
      }
      void navigate({ to: nextRoute });
    },
    onError: (err) => mapErrorToState(err, setFieldErrors, setTopError),
  });

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setFieldErrors({});
    setTopError(null);
    if (!firstName.trim()) {
      setFieldErrors({ first_name: "Required" });
      return;
    }
    save.mutate();
  }

  return (
    <OnboardingShell {...shell} title="Set up your profile">
      <p className="text-sm text-muted-foreground">
        We've pre-filled what we know from your invitation. Fill in the rest to continue.
      </p>
      <form onSubmit={onSubmit} aria-busy={save.isPending} noValidate className="space-y-4">
        <ReadonlyField label="Work email" value={user.email ?? ""} />
        {/* Slice 2: prefer the Org's canonical company_name; fall back
            to the legacy denormalised `user.company_name` for clients
            that haven't been backfilled yet. */}
        {(user.organization?.company_name ?? user.company_name) && (
          <ReadonlyField
            label="Company"
            value={user.organization?.company_name ?? user.company_name ?? ""}
          />
        )}

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FieldRow
            id="first_name"
            label="First name"
            value={firstName}
            onChange={setFirstName}
            error={fieldErrors.first_name}
            required
          />
          <FieldRow
            id="last_name"
            label="Last name"
            value={lastName}
            onChange={setLastName}
            error={fieldErrors.last_name}
          />
        </div>
        <FieldRow
          id="position"
          label="Job title"
          value={position}
          onChange={setPosition}
          error={fieldErrors.position}
        />
        <FieldRow
          id="phone"
          label="Mobile phone"
          value={phone}
          onChange={setPhone}
          error={fieldErrors.phone}
          placeholder="+61 412 345 678"
        />

        {topError && <FormError>{topError}</FormError>}
        <Button
          type="submit"
          loading={save.isPending}
          loadingLabel="Saving…"
          size="lg"
          className="w-full"
        >
          Next
        </Button>
      </form>
    </OnboardingShell>
  );
}

function splitFullName(full: string): { first: string; last: string } {
  const trimmed = full.trim();
  if (!trimmed) return { first: "", last: "" };
  const space = trimmed.indexOf(" ");
  if (space === -1) return { first: trimmed, last: "" };
  return { first: trimmed.slice(0, space), last: trimmed.slice(space + 1) };
}

interface FieldRowProps {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  error?: string;
  required?: boolean;
  placeholder?: string;
}

function FieldRow({ id, label, value, onChange, error, required, placeholder }: FieldRowProps) {
  return (
    <div className="space-y-2">
      <Label
        htmlFor={`ob-${id}`}
        className="text-xs font-medium uppercase tracking-[0.12em] text-muted-foreground"
      >
        {label}
        {required ? " *" : ""}
      </Label>
      <Input
        id={`ob-${id}`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-required={required ? "true" : undefined}
        aria-invalid={Boolean(error)}
        placeholder={placeholder}
      />
      {error && (
        <p role="alert" className="text-xs text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}

function ReadonlyField({ label, value }: { label: string; value: string }) {
  return (
    <dl className="space-y-2">
      <dt className="text-xs font-medium uppercase tracking-[0.12em] text-muted-foreground">
        {label}
      </dt>
      <dd className="rounded-md border bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
        {value}
      </dd>
    </dl>
  );
}

function mapErrorToState(
  err: unknown,
  setFieldErrors: (m: Record<string, string>) => void,
  setTopError: (s: string | null) => void,
) {
  if (err instanceof ApiError) {
    if (err.status === 422 && err.body && typeof err.body === "object") {
      const detail = (err.body as { detail?: unknown }).detail;
      if (Array.isArray(detail)) {
        const fieldMap: Record<string, string> = {};
        for (const d of detail as Array<{ loc?: unknown; msg?: string }>) {
          const loc = Array.isArray(d.loc) ? d.loc : [];
          const field = loc[loc.length - 1];
          // nosemgrep: javascript.express.security.audit.remote-property-injection.remote-property-injection -- low risk: `field` is the server's own 422 validation `loc` (FastAPI error path), string-guarded, written to a display-only error map. CHP-447 M2 baseline triage.
          if (typeof field === "string" && d.msg) fieldMap[field] = d.msg;
        }
        setFieldErrors(fieldMap);
        setTopError("Please fix the highlighted fields.");
        return;
      }
    }
  }
  setTopError("Couldn't save your details. Try again.");
}
